import {Suspense, type PropsWithChildren} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ForYouItemError, ForYouItemSkeleton} from './ForYouItemFallbacks'
import {useReviewers} from '../../data/use-reviewers'
import {useViewerRoles} from './use-viewer-roles'

export type ForYouAudience = 'author' | 'reviewer'

/**
 * Whether the current viewer "owns" the pull request — either as its author or as an assignee.
 *
 * Being an assignee covers scenarios like:
 *    - the PR is authored by a bot or other non-human account, and the viewer is the human responsible for it.
 *   - the PR is authored by a separate human, but the viewer is taking over responsibility for shipping it (e.g., the author is on vacation).
 *
 * Reads the overview route's assignees query, so this suspends.
 */
export function useViewerOwnsPullRequest(): boolean {
  const {isAuthor, isAssignee} = useViewerRoles()
  return isAuthor || isAssignee
}

/**
 * Whether the current viewer is a "reviewer" of the pull request — i.e., they have
 * either an outstanding (pending) review request or a completed review, in any state
 * (approved, commented, changes requested, or dismissed).
 *
 * Only considers the viewer's own user-level participation; team review requests are
 * not treated as making the viewer an individual reviewer.
 */
export function useViewerIsReviewer(): boolean {
  const viewerLogin = getCurrentUserLogin()
  const {data: reviewers} = useReviewers()

  if (!viewerLogin) return false

  const isViewer = (login: string | undefined) => login === viewerLogin

  const hasPendingRequest = (reviewers.pendingReviewRequests ?? []).some(
    request => request.reviewer?.reviewerType === 'User' && isViewer(request.reviewer.displayLogin),
  )
  if (hasPendingRequest) return true

  return (reviewers.completedReviews ?? []).some(
    review => review.reviewer?.reviewerType === 'User' && isViewer(review.reviewer.displayLogin),
  )
}

/**
 * Error boundary + suspense wrapper for a single ForYou item. When `audience` is
 * provided, the item only renders for the matching viewer; otherwise it always
 * renders. The `author` audience includes assignees; the `reviewer` audience is
 * anyone with a pending review request or a completed review (in any state).
 */
export function ForYouItemBoundary({
  children,
  name,
  audience,
}: PropsWithChildren<{name: string; audience?: ForYouAudience}>) {
  const boundary = (
    <ErrorBoundary boundaryName={`ForYouItem.${name}`} fallback={<ForYouItemError />}>
      <Suspense fallback={<ForYouItemSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  )

  return audience ? <AudienceGate audience={audience}>{boundary}</AudienceGate> : boundary
}

/** Gates on whether the viewer "owns" the pull request (author or assignee) or is a reviewer. */
function AudienceGate({audience, children}: PropsWithChildren<{audience: ForYouAudience}>) {
  const viewerIsOwner = useViewerOwnsPullRequest()
  const viewerIsReviewer = useViewerIsReviewer()
  const matchesAudience = audience === 'author' ? viewerIsOwner : viewerIsReviewer

  return matchesAudience ? <>{children}</> : null
}
